/**
 * External Imports
 */
import { FC, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { connect, useDispatch } from 'react-redux';

import { NeuIcon, NeuNavigation } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { IMainNavLinkObject, mainNavLinkObjects } from '../../../Route';

import { RootState } from '../../../redux/store';
import {
  clearUser,
  setSessionId,
  openModal,
  setModalType,
  setModalAction
} from '../../../redux/actions';
import { useGetVersionQuery } from '../../../redux/apis/Admin.api';

import Events from '../../../services/Analytics';

import { useFeatures } from '../../../services/Features/features';

import { logAdHocClick } from '../../../utils/analyticsHelpers';
import { setLastPath } from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import { LinkObj } from '../../../config/interfaces';

/**
 * Style Imports
 */
import {
  AddNewButton,
  AdhocBtnContainer,
  DropDownContainer,
  LogoImg,
  LogoLink,
  MainToolbar,
  NavTabs,
  PopItem,
  PopContent,
  RightMainNavContainer,
  StyledLink,
  StyledNavTab,
  UserInfoContainer,
  UserControls,
  UserNavTab
} from './MainNav.styles';

/**
 * Static File Imports
 */
import packageInfo from '../../../../package.json';
import { useUserRolesRef } from '../../../services/UserRoles';
import HelpDropdown from '../../customDropdowns/navDropdown/HelpDropdown';

interface MainNavProps {
  authUserRoles: string[];
  firstName: string;
  lastName: string;
  userCoid: string;
}

const MainNav: FC<MainNavProps> = ({
  authUserRoles,
  firstName,
  lastName,
  userCoid
}) => {
  const { removeUser, signoutSilent } = useAuth();
  const dispatch = useDispatch();
  const {
    isEnterpriseAdmin,
    isPatientRounder,
    isCSCRounder,
    isCNEdRounder,
    isValidationRounder,
    isAidetValidationRounder,
    isBssrValidationRounder,
    isSafetyValidationRounder,
    isEmployeeRounder,
    isNonSupportRounder,
    isTemplateConfigurator,
    isUserAdmin
  } = useUserRolesRef();

  const [popoverState, setShowPopover] = useState(false);
  const [apiVersionString, setApiVersionString] = useState(packageInfo.version);

  const { logTrackingEvent } = useAnalyticsApi();
  const { isCNEdEnabled } = useFeatures();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Query for API version on mount
  const {
    data: versionData
    // isError: versionError,
    // isLoading: isVersionLoading,
  } = useGetVersionQuery(undefined, {
    refetchOnMountOrArgChange: 60
  });

  const accountPath =
    isNonSupportRounder || isTemplateConfigurator || isUserAdmin
      ? '/account/facility-and-unit-settings'
      : isCSCRounder
      ? '/account/employee-rounding-settings/csc-list'
      : isCNEdRounder
      ? '/account/employee-rounding-settings/cned-list'
      : isValidationRounder
      ? '/account/employee-rounding-settings/nlr-validation-list'
      : isAidetValidationRounder
      ? '/account/employee-rounding-settings/aidet-validation-list'
      : isBssrValidationRounder
      ? '/account/employee-rounding-settings/bssr-validation-list'
      : isSafetyValidationRounder
      ? '/account/employee-rounding-settings/safety-validation-list'
      : '/account/facility-and-unit-settings';

  const handleVersionChange = () => {
    if (versionData && apiVersionString === packageInfo.version) {
      setApiVersionString(`API version ${versionData}`);
    } else {
      setApiVersionString(packageInfo.version);
    }
  };

  const logOutUser = () => {
    window.localStorage.removeItem('authToken');
    window.localStorage.removeItem('orbitLastPath');
    window.localStorage.removeItem('34');
    sessionStorage.removeItem('sessionId');
    dispatch(setSessionId(''));
    dispatch(clearUser());
    removeUser();
    signoutSilent();
    navigate('/login');
    console.log('User Logged Out');
  };

  const toggleModal = (mType: string) => {
    if (
      mType === 'Add Issue' ||
      mType === 'Add Feedback' ||
      mType === 'Add Stoplight'
    ) {
      logTrackingEvent(logAdHocClick(mType, 'add'));
      dispatch(setModalAction('UNIVERSAL-ADD'));
    }
    dispatch(setModalType(mType));
    dispatch(openModal());
    setShowPopover(!popoverState);
  };

  const returnRouteLinks = (route: LinkObj, isAllowed: boolean) => {
    const trimRoute = route.url.split('/')[1]; // take first part of pathname

    if (route.navItemText && isAllowed) {
      return (
        <StyledLink
          key={`${route.navItemText}"-Tab"`}
          id={`${route.navItemText}"-Tab"`}
          data-testid={`${route.navItemText}"-Tab"`}
          to={route.url}
          onClick={() => {
            if (route.navItemText === 'Employees') {
              logTrackingEvent([
                {
                  module: Events.Employee.Module,
                  screen: Events.Employee.Screen.DirectReports.Name,
                  eventName: null,
                  eventType: 'start',
                  org: {
                    orgId: userCoid,
                    orgLevel: 'Facility'
                  }
                }
              ]);
            } else if (route.navItemText === 'Patients') {
              logTrackingEvent([
                {
                  module: Events.Patient.Module,
                  screen: Events.Patient.Screen.Census.Name,
                  eventName: null,
                  eventType: 'start',
                  org: {
                    orgId: userCoid,
                    orgLevel: 'Facility'
                  }
                }
              ]);
            } else if (route.navItemText === 'Validation') {
              logTrackingEvent([
                {
                  module: Events.NLRValidation.Module,
                  screen: Events.NLRValidation.Screen.List.Name,
                  eventName: null,
                  eventType: 'start',
                  org: {
                    orgId: userCoid,
                    orgLevel: 'Facility'
                  }
                }
              ]);
            }
            setLastPath(route.url);
          }}
        >
          <StyledNavTab
            active={pathname.split('/')[1]?.includes(trimRoute)}
            unit="tab"
          >
            {route.navItemText}
          </StyledNavTab>
        </StyledLink>
      );
    }
    return null;
  };

  const createLinks = (links: IMainNavLinkObject[]) => {
    if (authUserRoles) {
      const approvedRoutes: (JSX.Element | null)[] = [];
      links.forEach(routeItem => {
        let isAllowed = false;
        if (
          routeItem.allowedRoles.some(role => authUserRoles?.includes(role))
        ) {
          isAllowed = true;
          if (routeItem.component) {
            approvedRoutes.push(routeItem.component);
          } else {
            approvedRoutes.push(returnRouteLinks(routeItem, isAllowed));
          }
        }
      });
      return approvedRoutes;
    }
    return null;
  };

  const isAdHocEnabled = useMemo(
    () =>
      isPatientRounder || isEmployeeRounder || (isCNEdRounder && isCNEdEnabled),
    [isPatientRounder, isEmployeeRounder, isCNEdRounder, isCNEdEnabled]
  );

  return (
    <MainToolbar id="MainNav" label="" unit="toolbar" collapse={false}>
      <LogoLink href="#!" aria-label="Title Link to Homepage">
        <div
          className="d-flex align-items-center neu-text-gray-0"
          style={{ minWidth: '9em' }}
        >
          <LogoImg
            height="40px"
            src="/assets/Orbit_Logo.png"
            alt="Orbit Logo"
          />
          <div className="d-flex flex-column justify-content-center ms-1">
            <span className="bold">Orbit</span>
            <span
              role="button"
              tabIndex={0}
              style={{ fontSize: 10, cursor: 'pointer' }}
              onBlur={
                versionData && apiVersionString !== packageInfo.version
                  ? () => handleVersionChange()
                  : undefined
              }
              onClick={handleVersionChange}
              onKeyDown={handleVersionChange}
            >
              {apiVersionString}
            </span>
          </div>
        </div>
      </LogoLink>
      <NeuNavigation unit="menu" color="blue" label="">
        <NavTabs
          className="d-md-block ms-1 d-md-left"
          color="orange"
          kind="blue"
          unit="group"
        >
          {createLinks(mainNavLinkObjects)}
          {(isPatientRounder || isEmployeeRounder) && (
            <StyledNavTab
              onClick={() =>
                window.open('https://resolve.hcahealthcare.cloud/', '_blank')
              }
              unit="tab"
            >
              <div
                id="Resolve-Icon"
                className="d-flex align-items-center flex-row-reverse justify-content-space-between"
              >
                <NeuIcon className="ms-1">launch</NeuIcon>
                Resolve
              </div>
            </StyledNavTab>
          )}
          <HelpDropdown key="Help-Tab" />
          {/* <QuickLinksDropdown key="QuickLinks-Tab" /> */}
        </NavTabs>
      </NeuNavigation>
      <RightMainNavContainer>
        {isAdHocEnabled && (
          <AdhocBtnContainer
            id="Universal-Add-New-Btn"
            style={{ position: 'relative' }}
            onBlur={() => setShowPopover(false)}
          >
            <AddNewButton
              data-testid="universal-add-new-btn"
              icon="add"
              size="small"
              onClick={() => setShowPopover(true)}
            >
              Add New
            </AddNewButton>

            <DropDownContainer showPopover={popoverState}>
              <PopItem
                id="Universal-Add-Issue"
                className="dropdown-hover-item"
                role="option"
                tabIndex={0}
                onMouseDown={() => toggleModal('Add Issue')}
              >
                <PopContent justify="start">
                  <NeuIcon className="me-1" color="blue" size="2x">
                    flag
                  </NeuIcon>
                  Add Issue
                </PopContent>
              </PopItem>
              <PopItem
                id="Universal-Add-Feedback"
                className="dropdown-hover-item"
                role="option"
                tabIndex={0}
                onMouseDown={() => toggleModal('Add Feedback')}
              >
                <PopContent justify="start">
                  <NeuIcon className="me-1" color="blue" size="2x">
                    campaign
                  </NeuIcon>
                  Add Feedback
                </PopContent>
              </PopItem>
              {(isEnterpriseAdmin || isPatientRounder || isEmployeeRounder) && (
                <PopItem
                  id="Universal-Add-Stoplight"
                  className="dropdown-hover-item"
                  role="option"
                  tabIndex={0}
                  onMouseDown={() => toggleModal('Add Stoplight')}
                >
                  <PopContent justify="start">
                    <NeuIcon className="me-1" color="blue" size="2x">
                      traffic
                    </NeuIcon>
                    Add Stoplight
                  </PopContent>
                </PopItem>
              )}
            </DropDownContainer>
          </AdhocBtnContainer>
        )}
        <UserControls id="User-Account">
          <UserInfoContainer>
            <p className="my-0">
              {firstName} {lastName} |{' '}
              <StyledLink
                className="h-auto"
                to={accountPath}
                onClick={() => setLastPath(accountPath)}
              >
                <UserNavTab
                  active={pathname.split('/')[1]?.includes('account')}
                >
                  Account
                </UserNavTab>
              </StyledLink>
            </p>
            {process.env.REACT_APP_ENV !== 'production' && (
              <UserNavTab
                className="my-0"
                onClick={logOutUser}
                onKeyDown={logOutUser}
              >
                Logout
              </UserNavTab>
            )}
          </UserInfoContainer>
          {/* <NeuAvatar
            color="gray"
            initials={`${firstName.charAt(0)}${lastName.charAt(0)}`}
            style={{ height: '40px', width: '40px' }}
          /> */}
          {/* Commenting out until Notifications are incorporated  */}
          {/* <NeuDrawerButton drawer="second" slot="notification">
            <NeuNotification>
            <NeuBadge slot="alerts" color="red">
            8
            </NeuBadge>
            </NeuNotification>
            </NeuDrawerButton> */}
        </UserControls>
      </RightMainNavContainer>
    </MainToolbar>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authUserRoles: state.AuthorizedUser.authorizedUser?.roles,
    modalState: state.ModalReducer.modal,
    userCoid: state.AuthorizedUser.authorizedUser.coid
  };
};

export default connect(mapReduxStateToProps)(MainNav);
